import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const TabContainer = styled.div`
	width: 100%;
`;

const TabHeader = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 24px;
	margin-bottom: 8px;
	position: relative;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
	background: none;
	border: none;
	padding: 12px 0;
	padding-bottom: 10px;
	font-size: 16px;
	font-weight: 500;
	color: ${(props) => (props.$isActive ? '#000000' : '#6B7280')};
	cursor: pointer;
	position: relative;
	transition: color 0.2s ease;
	outline: none;

	&:hover {
		color: #000000;
	}
`;

const UnderlineBar = styled.div<{ $left: number; $width: number }>`
	position: absolute;
	bottom: 0;
	height: 3px;
	background-color: #000000;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	left: ${(props) => props.$left}px;
	width: ${(props) => props.$width}px;
	border-radius: 1.5px 1.5px 0 0;
`;

const TabContent = styled.div<{ $isActive: boolean }>`
	display: ${(props) => (props.$isActive ? 'block' : 'none')};
	margin-top: 16px;
`;

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index } = props;

	return (
		<TabContent
			$isActive={value === index}
			role="tabpanel"
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
		>
			{children}
		</TabContent>
	);
}

interface Section {
	label: string;
	component: any;
}

interface TabbedInterface {
	sections: Section[];
	style?: React.CSSProperties;
	onChange?: (index: number) => void;
}

export default function Tabbed({ sections, style, onChange }: TabbedInterface) {
	const [value, setValue] = useState(0);
	const [barPosition, setBarPosition] = useState({ left: 0, width: 0 });
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const headerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Calculate position of the active tab
		const activeTab = tabRefs.current[value];
		if (activeTab && headerRef.current) {
			const headerRect = headerRef.current.getBoundingClientRect();
			const tabRect = activeTab.getBoundingClientRect();
			setBarPosition({
				left: tabRect.left - headerRect.left,
				width: tabRect.width,
			});
		}
	}, [value, sections]);

	// Calculate initial position on mount
	useEffect(() => {
		const timer = setTimeout(() => {
			const activeTab = tabRefs.current[0];
			if (activeTab && headerRef.current) {
				const headerRect = headerRef.current.getBoundingClientRect();
				const tabRect = activeTab.getBoundingClientRect();
				setBarPosition({
					left: tabRect.left - headerRect.left,
					width: tabRect.width,
				});
			}
		}, 100);
		return () => clearTimeout(timer);
	}, []);

	return (
		<TabContainer style={style}>
			<TabHeader ref={headerRef}>
				{sections.map((section: Section, index) => (
					<TabButton
						key={section.label}
						ref={(el) => (tabRefs.current[index] = el)}
						$isActive={value === index}
						onClick={() => {
							setValue(index);
							onChange?.(index);
						}}
						id={`simple-tab-${index}`}
						aria-controls={`simple-tabpanel-${index}`}
						aria-selected={value === index}
						role="tab"
					>
						{section.label}
					</TabButton>
				))}
				<UnderlineBar $left={barPosition.left} $width={barPosition.width} />
			</TabHeader>
			{sections.map((section: Section, index) => (
				<CustomTabPanel key={section.label} value={value} index={index}>
					{section.component}
				</CustomTabPanel>
			))}
		</TabContainer>
	);
}
